/**
 * V6.8权限检查组合式函数 - 基于新的核心层架构
 * 实现 {industry_code}:{role}:{module}:{action} 格式的权限校验
 */

import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { 
  V6Permission, 
  PermissionParser,
  PermissionMatcher 
} from '@/core/permission'
import { 
  UserProfileService,
  type UserProfile,
  UserPermissionResolver 
} from '@/core/user'
import { 
  createVuePermissionAdapter,
  type VuePermissionOptions 
} from '@/infrastructure/permission/VuePermissionAdapter'
import { 
  createPermissionGuard,
  createRoutePermissionChecker,
  createMenuPermissionFilter,
  createButtonPermissionChecker,
  createPermissionDirectiveFactory 
} from '@/infrastructure/permission/PermissionGuard'

// 用户信息接口（保持向后兼容）
interface UserInfo {
  id: string
  tenantId: string
  industryCode: string
  role: string
  username: string
  permissions: string[]
}

/**
 * V6.8权限检查组合式函数
 * 基于新的核心层架构，提供向后兼容的API
 */
export function useV6Permission(options: VuePermissionOptions = {}) {
  // 创建用户信息服务（简化版本，实际项目中应该注入）
  const userProfileService = new UserProfileService(
    {
      getCurrentUser: async () => ({
        id: 'user_001',
        tenantId: 'tenant_001',
        industryCode: 'AGR001',
        role: 'platform_business',
        username: '企业主张三',
        email: 'business@example.com',
        permissions: [
          '*:platform_business:content:view',
          '*:platform_business:diagnosis:view',
          'AGR001:platform_business:task:view'
        ]
      }),
      getUserById: async () => null,
      updateCurrentUserPreferences: async () => {}
    },
    {
      getUserPermissions: async () => []
    }
  )

  // 创建Vue权限适配器
  const adapter = createVuePermissionAdapter(userProfileService, {
    autoCheckRoute: true,
    showPermissionWarnings: true,
    cacheDuration: 5 * 60 * 1000,
    defaultUserRole: 'platform_business',
    defaultIndustryCode: 'AGR001',
    ...options
  })

  // 创建各种工具函数
  const routePermissionChecker = createRoutePermissionChecker(adapter)
  const menuPermissionFilter = createMenuPermissionFilter(adapter)
  const buttonPermissionChecker = createButtonPermissionChecker(adapter)
  const permissionDirectiveFactory = createPermissionDirectiveFactory(adapter)

  // 向后兼容的API
  const currentUser = computed(() => {
    const user = adapter.getCurrentUser().value
    if (!user) return null
    
    return {
      id: user.id,
      tenantId: user.tenantId,
      industryCode: user.industryCode,
      role: user.role,
      username: user.username,
      permissions: user.permissions
    } as UserInfo
  })

  /**
   * 检查用户是否有指定权限（向后兼容）
   */
  const hasPermission = (permission: V6Permission | string): boolean => {
    return adapter.hasPermission(permission)
  }

  /**
   * 权限检查并抛出异常（向后兼容）
   */
  const checkPermission = (permission: V6Permission | string): void => {
    if (!adapter.hasPermission(permission)) {
      throw new Error(`权限不足: ${typeof permission === 'string' ? permission : permission.toString()}`)
    }
  }

  /**
   * 检查路由权限（向后兼容）
   */
  const checkRoutePermission = (routePermissions: string[]): boolean => {
    return adapter.checkRoutePermission(routePermissions)
  }

  /**
   * 获取用户可访问的菜单项（向后兼容）
   */
  const getAccessibleMenuItems = (menuItems: any[]): any[] => {
    return menuPermissionFilter(menuItems)
  }

  /**
   * 按钮级别的权限检查（向后兼容）
   */
  const useButtonPermission = (permission: V6Permission | string) => {
    return adapter.useButtonPermission(permission)
  }

  /**
   * 行业隔离检查（向后兼容）
   */
  const canAccessIndustry = (industryCode: string): boolean => {
    return adapter.canAccessIndustry(industryCode)
  }

  /**
   * 获取用户角色信息（向后兼容）
   */
  const getUserRoleInfo = adapter.getUserRoleInfo()

  /**
   * 模拟用户登录（向后兼容）
   */
  const login = async (userInfo: Partial<UserInfo>) => {
    await adapter.login({
      id: userInfo.id || 'user_001',
      tenantId: userInfo.tenantId || 'tenant_001',
      industryCode: userInfo.industryCode || 'AGR001',
      role: userInfo.role || 'platform_business',
      username: userInfo.username || '分析师张三',
      permissions: userInfo.permissions || [
        '*:platform_business:content:view',
        '*:platform_business:diagnosis:view'
      ]
    })
  }

  /**
   * 模拟用户退出（向后兼容）
   */
  const logout = (): void => {
    adapter.logout()
  }

  /**
   * 初始化用户信息（向后兼容）
   */
  const initUserInfo = async (): Promise<void> => {
    await adapter.refreshUserInfo()
  }

  // 新API（扩展功能）
  const hasAnyPermission = (permissions: (V6Permission | string)[]): boolean => {
    return adapter.hasAnyPermission(permissions)
  }

  const hasAllPermissions = (permissions: (V6Permission | string)[]): boolean => {
    return adapter.hasAllPermissions(permissions)
  }

  const getAvailableIndustries = async (): Promise<string[]> => {
    return adapter.getAvailableIndustries()
  }

  const getAvailableActions = (module: string): string[] => {
    return adapter.getAvailableActions(module)
  }

  const getPermissionTree = async () => {
    return adapter.getPermissionTree()
  }

  return {
    // 状态（向后兼容）
    currentUser,
    getUserRoleInfo,
    
    // 权限检查方法（向后兼容）
    hasPermission,
    checkPermission,
    checkRoutePermission,
    getAccessibleMenuItems,
    useButtonPermission,
    canAccessIndustry,
    
    // 登录/退出（向后兼容）
    login,
    logout,
    initUserInfo,
    
    // 新API（扩展功能）
    hasAnyPermission,
    hasAllPermissions,
    getAvailableIndustries,
    getAvailableActions,
    getPermissionTree,
    
    // 基础设施层工具
    adapter,
    routePermissionChecker,
    menuPermissionFilter,
    buttonPermissionChecker,
    permissionDirectiveFactory
  }
}

/**
 * 权限指令 - 用于模板中的权限控制（向后兼容）
 */
export const vPermission = {
  mounted(el: HTMLElement, binding: any) {
    const { hasPermission } = useV6Permission()
    
    if (!hasPermission(binding.value)) {
      el.style.display = 'none'
    }
  },
  updated(el: HTMLElement, binding: any) {
    const { hasPermission } = useV6Permission()
    
    if (hasPermission(binding.value)) {
      el.style.display = ''
    } else {
      el.style.display = 'none'
    }
  }
}

/**
 * 权限混入 - 用于组件中的权限检查（向后兼容）
 */
export const permissionMixin = {
  methods: {
    $hasPermission(permission: V6Permission | string): boolean {
      const { hasPermission } = useV6Permission()
      return hasPermission(permission)
    },
    
    $checkPermission(permission: V6Permission | string): void {
      const { checkPermission } = useV6Permission()
      checkPermission(permission)
    },
    
    $canAccessIndustry(industryCode: string): boolean {
      const { canAccessIndustry } = useV6Permission()
      return canAccessIndustry(industryCode)
    }
  }
}

/**
 * 创建路由权限守卫（新API）
 */
export function createV6PermissionGuard(options?: VuePermissionOptions) {
  // 开发阶段：简化权限验证，直接放行所有路由
  const simplifiedGuard = (to: any, from: any, next: any) => {
    console.log(`[Dev Mode] 访问路由: ${to.path}`)
    next()
  }
  
  return simplifiedGuard
  
  // 生产环境代码（暂时注释）
  // const { adapter } = useV6Permission(options)
  // return createPermissionGuard({
  //   adapter,
  //   loginRoute: '/login',
  //   forbiddenRoute: '/403',
  //   showWarningMessages: true
  // })
}